import "@/App.css";
import { useEffect, useRef, useState } from "react";

const LOGO_URL =
  "https://cdn-boiac.nitrocdn.com/trmYtJKUZSBEGOAKQrMqYSWXRnlhTVDi/assets/images/optimized/rev-359a1f1/www.latinofoodshow.com/wp-content/uploads/2018/01/Logo_Transparent.png";

const YOUTUBE_ID = "ZgQMYlOiLck";

const GALLERY = [
  "/gallery/nga26-1.jpg",
  "/gallery/nga26-2.jpg",
  "/gallery/nga26-3.jpg",
  "/gallery/nga26-4.jpg",
  "/gallery/nga26-5.jpg",
  "/gallery/nga26-6.jpg",
  "/gallery/nga26-7.jpg",
  "/gallery/nga26-8.jpg",
  "/gallery/nga26-9.jpg",
  "/gallery/wlc-main.jpg",
  "/gallery/wlc-16.jpg",
  "/gallery/wlc-22.jpg",
  "/gallery/wlc-18.jpg",
  "/gallery/wlc-29.jpg",
  "/gallery/wlc-28.jpg",
  "/gallery/wlc-27.jpg",
  "/gallery/wlc-26.jpg",
  "/gallery/wlc-25.jpg",
  "/gallery/wlc-24.jpg",
  "/gallery/wlc-17.jpg",
  "/gallery/wlc-19.jpg",
  "/gallery/wlc-21.jpg",
  "/gallery/wlc-15.jpg",
  "/gallery/wlc-14.jpg",
  "/gallery/wlc-13.jpg",
  "/gallery/show24-1.jpg",
  "/gallery/show24-2.jpg",
  "/gallery/show24-3.jpg",
  "/gallery/show24-4.jpg",
  "/gallery/show24-5.jpg",
  "/gallery/show24mar-1.jpg",
  "/gallery/show24mar-11.jpg",
  "/gallery/show24mar-111.jpg",
  "/gallery/show24mar-12.jpg",
];

function Reveal({ children, delay = 0, as: Tag = "div", className = "" }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "reveal-in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { href: "#nga", label: "NGA Show 2027" },
    { href: "#intel", label: "Market Intel" },
    { href: "#source", label: "Pavilion" },
    { href: "#gallery", label: "Gallery" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <header
      data-testid="site-navbar"
      className={`nav ${scrolled ? "nav-scrolled" : ""}`}
    >
      <a href="#top" className="nav-brand" data-testid="nav-brand">
        <img src={LOGO_URL} alt="World of the Latino Cuisine" />
        <span>
          <strong>Latino</strong> Food Show
        </span>
      </a>
      <nav className="nav-links">
        {links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            data-testid={`nav-link-${l.href.replace("#", "")}`}
          >
            {l.label}
          </a>
        ))}
        <a
          className="nav-cta"
          href="https://latino-food-intel.emergent.host"
          target="_blank"
          rel="noreferrer"
          data-testid="nav-cta-subscribe"
        >
          Subscribe
        </a>
      </nav>
      <button
        className="nav-burger"
        aria-label="menu"
        onClick={() => setOpen(!open)}
        data-testid="nav-burger"
      >
        <span /> <span /> <span />
      </button>
      {open && (
        <div className="nav-mobile" data-testid="nav-mobile">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)}>
              {l.label}
            </a>
          ))}
          <a
            href="https://latino-food-intel.emergent.host"
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            className="nav-cta"
          >
            Subscribe
          </a>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="hero" data-testid="hero-section">
      <div className="hero-grain" />
      <div className="hero-inner">
        <Reveal className="hero-text">
          <span className="eyebrow" data-testid="hero-eyebrow">
            World of the Latino Cuisine, Inc.
          </span>
          <h1 className="hero-title">
            Where the <em>Latino</em> food market
            <br />
            meets the U.S. grocery shelf.
          </h1>
          <p className="hero-sub">
            Twelve years connecting importers, exhibitors, distributors and
            buyers at the NGA Show — and bringing you the intelligence to act
            on the fastest-growing food economy in America.
          </p>
          <div className="hero-ctas">
            <a
              href="#nga"
              className="btn btn-primary"
              data-testid="hero-cta-nga"
            >
              Join the 2027 Pavilion
            </a>
            <a
              href="https://latino-food-intel.emergent.host"
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost"
              data-testid="hero-cta-intel"
            >
              Market Intelligence Report →
            </a>
          </div>
        </Reveal>

        <Reveal delay={120} className="hero-video-wrap">
          <div className="hero-video-frame" data-testid="hero-video">
            <iframe
              title="Latino Food Show"
              src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_ID}&controls=0&rel=0&modestbranding=1&playsinline=1&showinfo=0&iv_load_policy=3`}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              frameBorder="0"
            />
            <div className="hero-video-mask-top" />
            <div className="hero-video-mask-bottom" />
          </div>
          <div className="hero-video-tag">Live from the show floor</div>
        </Reveal>
      </div>

      <div className="hero-marquee" aria-hidden="true">
        <div className="marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="marquee-row">
              <span>NGA Show 2027</span>
              <span>•</span>
              <span>MGM Grand · Las Vegas</span>
              <span>•</span>
              <span>February 1–2, 2027</span>
              <span>•</span>
              <span>$4.0T U.S. Latino GDP</span>
              <span>•</span>
              <span>12th Consecutive Year</span>
              <span>•</span>
              <span>15 Exhibitor Spaces</span>
              <span>•</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NgaShow() {
  return (
    <section id="nga" className="section nga" data-testid="nga-section">
      <div className="section-grid">
        <Reveal className="nga-left">
          <span className="section-kicker">Pavilion · February 1–2, 2027</span>
          <h2 className="section-title">
            Join us at the <em>2027 NGA Show</em> in Las Vegas
          </h2>
          <p className="lead">
            YES! This year we are continuing our partnership — for the 12th
            consecutive year — with the National Grocers Association / Clarion
            Events, participating with a full pavilion at the NGA Show. The
            show will be held{" "}
            <strong>February 1–2, 2027, at the MGM Grand Hotel and
            Convention Center in Las Vegas</strong>{" "}
            (
            <a
              href="https://www.thengashow.com"
              target="_blank"
              rel="noreferrer"
            >
              www.thengashow.com
            </a>
            ). The show, and the Latino, Caribbean and International Pavilion
            has sold out every single year.
          </p>
          <p>
            Our pavilion at the 2027 NGA Show consists of{" "}
            <strong>15 exhibitor spaces offered at a special discount</strong>,
            first come — first served. The purpose of the Latino, Caribbean and
            International Pavilion is to offer grocers products in response to
            the growth of the Latino market everywhere, and their need to fill
            store shelves to satisfy customer demand.
          </p>
          <p>
            There is no better place than the NGA Show to find face-to-face
            networking opportunities with influential decision makers. Over the
            course of two and a half days, attendees can attend more than 65
            education workshops and visit an EXPO floor with over 400
            exhibitors. <strong>Buyers plan to spend a collective $600
            million at the show.</strong> Each year, over 5,000 independent
            supermarket owners, senior executives, and buyers attend.
          </p>

          <div className="nga-ctas">
            <a
              href="https://url.avanan.click/v2/r01/___https:/info.urban-expo.com/e/338151/-email-utm-campaign-rebooking1/5cknl7/1600611408/h/0ySEU4UJl_gDRIt4KcQVuiTt95UHqobNLHMivNf_ZnQ___.YXAzOm5vcnRoc2hvcmU6YTpvOjBjZjM2NjFlZmFlNjJmOGI0NWE2NGQ2MmI3NDJmYmRiOjc6MGE5NTpkNDg1ODMwODJjYjE4ODM0ZDBmZTE0MzlmYTg2Y2EzYTY4MzdiMjMzZDVjN2RjMDJmYzBjNjE5MmY0YTc4OGY1Omg6VDpG"
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary"
              data-testid="nga-cta-floorplan"
            >
              View 2027 Floor Plan
            </a>
            <a
              href="#contact"
              className="btn btn-outline"
              data-testid="nga-cta-contact"
            >
              Reserve a Booth
            </a>
          </div>
        </Reveal>

        <Reveal delay={100} className="nga-right">
          <div className="nga-card" data-testid="nga-stats-card">
            <div className="nga-card-row">
              <span className="big">5,000+</span>
              <span>Buyers, owners & executives in attendance</span>
            </div>
            <div className="nga-card-row">
              <span className="big">$600M</span>
              <span>Collective buying intent on the floor</span>
            </div>
            <div className="nga-card-row">
              <span className="big">400+</span>
              <span>Exhibitors across the EXPO</span>
            </div>
            <div className="nga-card-row">
              <span className="big">15</span>
              <span>Discounted booths in our 2027 pavilion</span>
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal className="nga-recap" delay={120}>
        <div className="nga-recap-head">
          <span className="section-kicker">NGA Show 2026 · Attendee Recap</span>
          <h3 className="nga-recap-title">
            Who you'll meet at the <em>2027</em> pavilion.
          </h3>
          <p className="nga-recap-sub">
            Preliminary 2026 data — a profile of the buyers, retailers and
            decision-makers walking the floor.
          </p>
        </div>

        <div className="nga-recap-grid">
          <div className="recap-hero" data-testid="recap-hero">
            <span className="recap-hero-num">84%</span>
            <span className="recap-hero-label">
              of attendees are involved in the purchase-making process
            </span>
          </div>

          <div className="recap-block" data-testid="recap-business-type">
            <h4>Attendee business type</h4>
            <ul className="recap-bars">
              <li>
                <span className="recap-bar-label">Retailers</span>
                <span className="recap-bar"><i style={{ width: "68%" }} /></span>
                <span className="recap-bar-pct">68%</span>
              </li>
              <li>
                <span className="recap-bar-label">Suppliers / Associates</span>
                <span className="recap-bar"><i style={{ width: "18%" }} /></span>
                <span className="recap-bar-pct">18%</span>
              </li>
              <li>
                <span className="recap-bar-label">Wholesalers</span>
                <span className="recap-bar"><i style={{ width: "10%" }} /></span>
                <span className="recap-bar-pct">10%</span>
              </li>
              <li>
                <span className="recap-bar-label">Government / Non-Profit</span>
                <span className="recap-bar"><i style={{ width: "5%" }} /></span>
                <span className="recap-bar-pct">5%</span>
              </li>
            </ul>
          </div>

          <div className="recap-block" data-testid="recap-retailer-size">
            <h4>Retailers by store count</h4>
            <ul className="recap-bars">
              <li>
                <span className="recap-bar-label">1 store</span>
                <span className="recap-bar"><i style={{ width: "27%" }} /></span>
                <span className="recap-bar-pct">27%</span>
              </li>
              <li>
                <span className="recap-bar-label">2 – 5 stores</span>
                <span className="recap-bar"><i style={{ width: "28%" }} /></span>
                <span className="recap-bar-pct">28%</span>
              </li>
              <li>
                <span className="recap-bar-label">6 – 15 stores</span>
                <span className="recap-bar"><i style={{ width: "23%" }} /></span>
                <span className="recap-bar-pct">23%</span>
              </li>
              <li>
                <span className="recap-bar-label">16 – 30 stores</span>
                <span className="recap-bar"><i style={{ width: "12%" }} /></span>
                <span className="recap-bar-pct">12%</span>
              </li>
              <li>
                <span className="recap-bar-label">31 – 70 stores</span>
                <span className="recap-bar"><i style={{ width: "5%" }} /></span>
                <span className="recap-bar-pct">5%</span>
              </li>
              <li>
                <span className="recap-bar-label">71 – 101+ stores</span>
                <span className="recap-bar"><i style={{ width: "7%" }} /></span>
                <span className="recap-bar-pct">7%</span>
              </li>
            </ul>
          </div>

          <div className="recap-block" data-testid="recap-sourcing">
            <h4>What they're sourcing</h4>
            <div className="recap-pair">
              <div>
                <span className="recap-pair-num">56%</span>
                <span>
                  Operational & support services — tech, store design,
                  equipment, corporate services
                </span>
              </div>
              <div>
                <span className="recap-pair-num">42%</span>
                <span>
                  On-shelf & retail products — center store, fresh, health,
                  wellness & seasonal
                </span>
              </div>
            </div>
          </div>

          <div className="recap-block recap-region" data-testid="recap-region">
            <h4>Where they come from</h4>
            <ul className="recap-region-grid">
              <li><span>25%</span> East North Central</li>
              <li><span>18%</span> Pacific</li>
              <li><span>12%</span> West South Central</li>
              <li><span>11%</span> West North Central</li>
              <li><span>10%</span> Middle Atlantic</li>
              <li><span>9%</span> South Atlantic</li>
              <li><span>8%</span> Mountain</li>
              <li><span>7%</span> East South Central</li>
              <li><span>4%</span> International</li>
              <li><span>3%</span> New England</li>
            </ul>
          </div>
        </div>

        <p className="nga-recap-footnote">
          Source: NGA Show 2026 Attendee Recap. Figures reflect preliminary
          2026 data and are subject to change.
        </p>
      </Reveal>
    </section>
  );
}

function MarketIntel() {
  return (
    <section
      id="intel"
      className="section intel"
      data-testid="intel-section"
    >
      <div className="intel-inner">
        <Reveal className="intel-head">
          <span className="section-kicker light">Market Intelligence</span>
          <h2 className="section-title light">
            A market <em>too large</em> to ignore.
          </h2>
          <p className="lead light">
            We invite you to subscribe to the World of the Latino Cuisine{" "}
            <strong>Market Intelligence Report</strong> — a bilingual monthly
            publication covering the U.S. Latino food and beverage market.
          </p>
        </Reveal>

        <div className="intel-grid">
          <Reveal delay={60} className="intel-body">
            <p>
              For many years, discussions about Hispanic foods focused
              primarily on demographics. Industry leaders recognized that the
              Hispanic population was growing but often viewed Latino consumers
              as one segment within a much larger marketplace.
            </p>
            <p>
              Today, that perspective is no longer sufficient. According to the{" "}
              <em>2025 Official U.S. Latino GDP Report</em> by the Latino Donor
              Collaborative, the U.S. Latino economy generated{" "}
              <strong>$4.0 trillion</strong> in economic output in 2023 —{" "}
              making it the <strong>fifth-largest economy in the world</strong>{" "}
              if measured independently. Latino purchasing power reached{" "}
              <strong>$4.1 trillion</strong>, while Latino income climbed to{" "}
              <strong>$3.1 trillion</strong>. Between 2015 and 2023 the Latino
              economy expanded at <strong>more than twice</strong> the growth
              rate of the broader U.S. economy.
            </p>
          </Reveal>

          <Reveal delay={140} className="intel-side">
            <div className="intel-card" data-testid="intel-card">
              <h3>What you get</h3>
              <ul>
                <li>Monthly bilingual briefing (EN / ES)</li>
                <li>Original articles & curated news summaries</li>
                <li>Imports, exports & retail trends</li>
                <li>Category trends & consumer insights</li>
                <li>Strategic growth opportunities</li>
              </ul>
              <a
                href="https://latino-food-intel.emergent.host"
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary btn-full"
                data-testid="intel-subscribe-btn"
              >
                Subscribe to the Report →
              </a>
              <span className="intel-card-note">
                For importers, exporters, supermarkets, distributors,
                manufacturers, trade-show exhibitors, and Latino food &
                beverage brands.
              </span>
            </div>
          </Reveal>
        </div>

        <Reveal delay={80} className="intel-stats">
          <div>
            <span className="big">$4.0T</span>
            <span>U.S. Latino economic output (2023)</span>
          </div>
          <div>
            <span className="big">$4.1T</span>
            <span>Latino purchasing power</span>
          </div>
          <div>
            <span className="big">5th</span>
            <span>Largest economy in the world, if measured alone</span>
          </div>
          <div>
            <span className="big">2×</span>
            <span>Growth rate vs. broader U.S. economy</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function SourcePillars() {
  return (
    <section
      id="source"
      className="section source"
      data-testid="source-section"
    >
      <Reveal>
        <span className="section-kicker">Pavilion Advantages</span>
        <h2 className="section-title">
          Source Latino products at the <em>NGA Show</em> in Las Vegas.
        </h2>
      </Reveal>

      <div className="pillars">
        {[
          {
            t: "Grow",
            d: "Expose your product to an international audience of buyers, importers and chain executives — all in one floor.",
          },
          {
            t: "Stand Out",
            d: "Exhibit your best alongside curated Latino, Caribbean and International brands — and guarantee the best.",
          },
          {
            t: "Learn",
            d: "Explore the hidden wonders of the Latino cuisine through tastings, demonstrations and authentic conversations.",
          },
        ].map((p, i) => (
          <Reveal key={p.t} delay={i * 100} className="pillar">
            <h3>{p.t}</h3>
            <p>{p.d}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section id="gallery" className="section gallery" data-testid="gallery-section">
      <Reveal>
        <span className="section-kicker">From the Floor</span>
        <h2 className="section-title">
          Moments from the <em>Latino Food Show</em>, the NGA Show and our
          Events.
        </h2>
      </Reveal>
      <div className="gallery-grid">
        {GALLERY.map((src, i) => (
          <Reveal key={src} delay={i * 40} className="gallery-tile">
            <img src={src} alt={`Pavilion ${i + 1}`} loading="lazy" />
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section
      id="contact"
      className="section contact"
      data-testid="contact-section"
    >
      <Reveal className="contact-inner">
        <span className="section-kicker light">Get in touch</span>
        <h2 className="section-title light">
          For booth information <em>and sales.</em>
        </h2>
        <div className="contact-card" data-testid="contact-card">
          <div className="contact-name">Bill Colon</div>
          <a
            className="contact-row"
            href="mailto:latinofoodshow1@gmail.com"
            data-testid="contact-email"
          >
            <span className="contact-label">Email</span>
            <span className="contact-value">latinofoodshow1@gmail.com</span>
          </a>
          <a
            className="contact-row"
            href="tel:+12014010291"
            data-testid="contact-phone"
          >
            <span className="contact-label">Telephone</span>
            <span className="contact-value">201-401-0291</span>
          </a>
          <a
            className="btn btn-primary btn-full"
            href="mailto:latinofoodshow1@gmail.com?subject=2027%20NGA%20Pavilion%20Booth%20Inquiry"
            data-testid="contact-cta"
          >
            Request Booth Information
          </a>
        </div>
      </Reveal>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer" data-testid="site-footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <img src={LOGO_URL} alt="World of the Latino Cuisine" />
          <p>
            World of the Latino Cuisine, Inc. — connecting Latino brands with
            mainstream U.S. grocery since 2014.
          </p>
        </div>
        <div className="footer-cols">
          <div>
            <h4>Pavilion</h4>
            <a href="#nga">2027 NGA Show</a>
            <a
              href="https://url.avanan.click/v2/r01/___https:/info.urban-expo.com/e/338151/-email-utm-campaign-rebooking1/5cknl7/1600611408/h/0ySEU4UJl_gDRIt4KcQVuiTt95UHqobNLHMivNf_ZnQ___.YXAzOm5vcnRoc2hvcmU6YTpvOjBjZjM2NjFlZmFlNjJmOGI0NWE2NGQ2MmI3NDJmYmRiOjc6MGE5NTpkNDg1ODMwODJjYjE4ODM0ZDBmZTE0MzlmYTg2Y2EzYTY4MzdiMjMzZDVjN2RjMDJmYzBjNjE5MmY0YTc4OGY1Omg6VDpG"
              target="_blank"
              rel="noreferrer"
            >
              Floor Plan
            </a>
            <a href="#contact">Reserve a booth</a>
          </div>
          <div>
            <h4>Intelligence</h4>
            <a
              href="https://latino-food-intel.emergent.host"
              target="_blank"
              rel="noreferrer"
            >
              Subscribe to the Report
            </a>
            <a href="#intel">Why it matters</a>
          </div>
          <div>
            <h4>Contact</h4>
            <a href="mailto:latinofoodshow1@gmail.com">
              latinofoodshow1@gmail.com
            </a>
            <a href="tel:+12014010291">201-401-0291</a>
          </div>
        </div>
      </div>
      <div className="footer-base">
        © {new Date().getFullYear()} World of the Latino Cuisine, Inc. · All
        rights reserved.
      </div>
    </footer>
  );
}

function App() {
  return (
    <div className="app-shell" data-testid="app-shell">
      <NavBar />
      <Hero />
      <NgaShow />
      <MarketIntel />
      <SourcePillars />
      <Gallery />
      <Contact />
      <Footer />
    </div>
  );
}

export default App;
